/**
 * Compte à rebours « MM JJ HH » utilisé sur les cartes d'objectif.
 *
 * - MM = nombre de mois complets restants
 * - JJ = nombre de jours restants après retrait des mois
 * - HH = nombre d'heures restantes après retrait des jours
 *
 * Quand la deadline est passée, on renvoie `{ expired: true }`.
 */

/**
 * Niveau d'urgence d'une deadline, utilisé pour colorer le badge de countdown.
 *
 * - `normal`  : > 1 mois — fond blanc, texte sombre
 * - `soon`    : ≤ 1 mois — fond vert, texte blanc
 * - `urgent`  : ≤ 1 semaine — fond orange, texte blanc
 * - `critical`: ≤ 24h — fond rouge, texte blanc
 * - `expired` : passée — fond rouge foncé, texte blanc
 */
export type DeadlineUrgency = 'normal' | 'soon' | 'urgent' | 'critical' | 'expired';

export interface DeadlineCountdown {
  expired: boolean;
  months: number;
  days: number;
  hours: number;
  /** Représentation formatée : "02M 14J 03H" ou "Échue". */
  formatted: string;
  /** Niveau d'urgence pour la couleur du badge. */
  urgency: DeadlineUrgency;
  /** Conservé pour rétro-compat : vrai dès `urgent` (≤ 7 jours). */
  urgent: boolean;
}

const MS_PER_HOUR = 60 * 60 * 1000;
const APPROX_DAYS_PER_MONTH = 30;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function computeDeadlineCountdown(
  deadlineIso: string | null | undefined,
  now: Date = new Date(),
): DeadlineCountdown | null {
  if (!deadlineIso) return null;
  const target = new Date(deadlineIso);
  if (Number.isNaN(target.getTime())) return null;

  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) {
    return {
      expired: true,
      months: 0,
      days: 0,
      hours: 0,
      formatted: 'Échue',
      urgency: 'expired',
      urgent: true,
    };
  }

  const totalHours = Math.floor(diffMs / MS_PER_HOUR);
  const totalDays = Math.floor(totalHours / 24);

  const months = Math.floor(totalDays / APPROX_DAYS_PER_MONTH);
  const days = totalDays - months * APPROX_DAYS_PER_MONTH;
  const hours = totalHours - totalDays * 24;

  let urgency: DeadlineUrgency;
  if (totalHours < 24) urgency = 'critical';
  else if (totalDays < 7) urgency = 'urgent';
  else if (totalDays < APPROX_DAYS_PER_MONTH) urgency = 'soon';
  else urgency = 'normal';

  const parts: string[] = [];
  if (months > 0) parts.push(`${pad2(months)}M`);
  parts.push(`${pad2(days)}J`);
  parts.push(`${pad2(hours)}H`);

  return {
    expired: false,
    months,
    days,
    hours,
    formatted: parts.join(' '),
    urgency,
    urgent: urgency === 'urgent' || urgency === 'critical',
  };
}

/**
 * Convertit une valeur d'input HTML (`<input type="datetime-local">` au format
 * `YYYY-MM-DDTHH:mm`) en ISO 8601 UTC à stocker en base.
 */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Convertit une deadline ISO 8601 (timezone aware) au format d'input HTML
 * `datetime-local` (heure locale, sans timezone).
 */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

/**
 * À partir d'une date `YYYY-MM-DD`, retourne la valeur d'input
 * `datetime-local` correspondante à 08:00 (heure par défaut admin).
 */
export function dateToDefaultDeadlineInput(date: string): string {
  if (!date) return '';
  return `${date}T08:00`;
}

/**
 * Classes Tailwind pour le badge de countdown selon le niveau d'urgence.
 * Toujours `border + bg + text` ; les variants colorés utilisent `text-white`.
 */
export function getDeadlineBadgeClasses(urgency: DeadlineUrgency): string {
  switch (urgency) {
    case 'expired':
      return 'border-red-700 bg-red-700 text-white shadow';
    case 'critical':
      return 'border-red-500 bg-red-500 text-white shadow';
    case 'urgent':
      return 'border-amber-500 bg-amber-500 text-white shadow';
    case 'soon':
      return 'border-emerald-500 bg-emerald-500 text-white shadow';
    case 'normal':
    default:
      return 'border-ink-200 bg-white/90 text-ink-800 backdrop-blur shadow-sm';
  }
}

/**
 * Formate une deadline ISO 8601 au format date/heure classique fr-FR.
 * Ex. "27 juin 2026 à 08:00".
 */
export function formatDeadlineLong(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${date} à ${time}`;
}
